import { StateMachine } from "@/core/arch/state_machine";
import type {
  EventsRepository,
  ConnectedListOpts,
  ListOpts,
} from "./events_repository";
import type {
  UnifiedEvent,
  EventFilters,
  DateRangeFilter,
  EventSource,
} from "./types";

// ── State ─────────────────────────────────────────────────────────────────────

export interface EventsListState {
  source: EventSource;
  /** All filtered items — DataTable handles pagination and sorting. */
  items: UnifiedEvent[];
  /** Total count of items after client-side filters are applied. */
  totalCount: number;
  filters: EventFilters;
  status: "idle" | "loading" | "loaded" | "error";
  error?: string;
}

// ── Defaults ──────────────────────────────────────────────────────────────────

const DEFAULT_DATE_RANGE: DateRangeFilter = { preset: "24h" };

/** Page size used when looping through all server pages. */
const SERVER_PAGE_SIZE = 500;

function defaultFilters(overrides?: Partial<EventFilters>): EventFilters {
  return {
    typeQuery: "",
    dateRange: DEFAULT_DATE_RANGE,
    livemode: "all",
    account: null,
    statusFilter: [],
    ...overrides,
  };
}

// ── Scoped-mode options ────────────────────────────────────────────────────────

export interface ScopedModeOptions {
  /** Fixed source tab — no source switching allowed */
  source: EventSource;
  /** Pre-fixed account filter (Connected tab only) */
  account?: string | null;
}

// ── BLoC ─────────────────────────────────────────────────────────────────────

export class EventsListBloc extends StateMachine<EventsListState> {
  private readonly scopedMode: ScopedModeOptions | null;

  /**
   * Monotonic token used to guard against stale-response races: if two
   * `load()` calls are in flight, only the most-recent one is permitted to
   * commit its result. Without this, a slow older request could overwrite a
   * newer request's results when its promise finally resolves.
   */
  private _loadToken = 0;

  /** Full list of events from the last fetch. Client filters are applied from this. */
  private _allItems: UnifiedEvent[] = [];

  constructor(
    private repo: EventsRepository,
    opts?: { scopedMode?: ScopedModeOptions },
  ) {
    const source = opts?.scopedMode?.source ?? "platform";
    const account = opts?.scopedMode?.account ?? null;
    super({
      source,
      items: [],
      totalCount: 0,
      filters: defaultFilters({ account }),
      status: "idle",
    });
    this.scopedMode = opts?.scopedMode ?? null;
  }

  // ── Public actions ──────────────────────────────────────────────────────────

  /**
   * Fetch ALL events matching the current filters by looping through pages,
   * cache the full list, and emit the full filtered array.
   */
  async load(): Promise<void> {
    const token = ++this._loadToken;
    this.setState({ ...this.state, status: "loading", error: undefined });

    try {
      const allItems = await this._fetchAll();

      // eslint-disable-next-line security/detect-possible-timing-attacks -- request-sequencing counter, not a secret comparison
      if (token !== this._loadToken) return;

      this._allItems = allItems;
      const filtered = this._applyClientFilters(this._allItems);
      this.setState({
        ...this.state,
        items: filtered,
        totalCount: filtered.length,
        status: "loaded",
        error: undefined,
      });
    } catch (err) {
      // eslint-disable-next-line security/detect-possible-timing-attacks -- request-sequencing counter, not a secret comparison
      if (token !== this._loadToken) return;
      this.setState({
        ...this.state,
        status: "error",
        error: err instanceof Error ? err.message : "Failed to load events",
      });
    }
  }

  /**
   * Switch source tab. Clears state and re-initializes filters.
   * No-op if in scoped mode (source is fixed).
   */
  setSource(source: EventSource): void {
    if (this.scopedMode) return;
    if (this.state.source === source) return;
    this._allItems = [];
    this.setState({
      ...this.state,
      source,
      items: [],
      totalCount: 0,
      filters: defaultFilters(),
      status: "idle",
      error: undefined,
    });
  }

  /**
   * Apply a filter patch and re-fetch for server-side filters.
   *
   * `typeQuery` and `statusFilter` are applied purely client-side via
   * `_applyClientFilters`, so patches that only touch those fields do not
   * require a network round-trip.
   */
  async setFilter(patch: Partial<EventFilters>): Promise<void> {
    const keys = Object.keys(patch);
    const clientSideOnly = new Set(["typeQuery", "statusFilter"]);
    const onlyClientSide =
      keys.length > 0 && keys.every((k) => clientSideOnly.has(k));

    const newFilters = { ...this.state.filters, ...patch };

    if (onlyClientSide) {
      const filtered = this._applyClientFilters(this._allItems, newFilters);
      this.setState({
        ...this.state,
        filters: newFilters,
        items: filtered,
        totalCount: filtered.length,
      });
      return;
    }

    this.setState({ ...this.state, filters: newFilters });
    await this.load();
  }

  // ── Private helpers ─────────────────────────────────────────────────────────

  private _applyClientFilters(
    items: UnifiedEvent[],
    filters = this.state.filters,
  ): UnifiedEvent[] {
    const q = filters.typeQuery.toLowerCase();
    const statusFilter = filters.statusFilter;
    const hasTypeFilter = q.length > 0;
    const hasStatusFilter = statusFilter.length > 0;

    if (!hasTypeFilter && !hasStatusFilter) return items;

    return items.filter((e) => {
      if (hasTypeFilter && !e.type.toLowerCase().includes(q)) return false;
      if (hasStatusFilter) {
        const effectiveStatus = e.processingStatus ?? "unknown";
        if (!statusFilter.includes(effectiveStatus as never)) return false;
      }
      return true;
    });
  }

  /**
   * Loop through all server pages for the current source/filters, accumulating
   * all events until nextToken is exhausted.
   */
  private async _fetchAll(): Promise<UnifiedEvent[]> {
    const { source, filters } = this.state;
    const accumulated: UnifiedEvent[] = [];
    let nextToken: string | null | undefined = undefined;

    do {
      let result: { items: UnifiedEvent[]; nextToken: string | null };

      if (source === "platform") {
        const opts: ListOpts = {
          filters,
          limit: SERVER_PAGE_SIZE,
          nextToken: nextToken ?? undefined,
        };
        result = await this.repo.listPlatform(opts);
      } else if (source === "store") {
        const opts: ListOpts = {
          filters,
          limit: SERVER_PAGE_SIZE,
          nextToken: nextToken ?? undefined,
        };
        result = await this.repo.listStore(opts);
      } else {
        const opts: ConnectedListOpts = {
          filters,
          limit: SERVER_PAGE_SIZE,
          account: filters.account ?? undefined,
          nextToken: nextToken ?? undefined,
        };
        result = await this.repo.listConnected(opts);
      }

      accumulated.push(...result.items);
      nextToken = result.nextToken;
    } while (nextToken !== null && nextToken !== undefined);

    return accumulated;
  }
}
